<template>
  <div class="drag-drop-game">
    <!-- Show LevelComplete component when the game is completed -->
    <LevelComplete v-if="gameCompleted" />

    <!-- Show the game area when the game is not completed -->
    <div v-else>
      <h1 class="instruction">
        Drop the fruits and vegetables into the correct baskets
      </h1>
      <div class="game-area">
        <div class="item-list">
          <div
            v-for="(item, index) in items"
            :key="index"
            class="draggable-item"
            @mousedown="startDrag($event, item)"
            @touchstart.prevent="startDrag($event, item)"
            @click="speakOutLoud(item.name)"
          >
            {{ getItemDisplay(item) }}
          </div>
        </div>
        <div class="baskets">
          <div
            id="fruit-basket"
            class="basket"
            data-basket-type="fruit"
          >
            🧺 Fruits
            <div class="basket-items">
              <div
                v-for="(item, index) in fruitBasket"
                :key="index"
                class="basket-item"
              >
                {{ getItemDisplay(item) }}
              </div>
            </div>
          </div>
          <div
            id="vegetable-basket"
            class="basket"
            data-basket-type="vegetable"
          >
            🧺 Vegetables
            <div class="basket-items">
              <div
                v-for="(item, index) in vegetableBasket"
                :key="index"
                class="basket-item"
              >
                {{ getItemDisplay(item) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
// Import the LevelComplete component
import LevelComplete from '@/components/LevelComplete.vue';
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      items: [
        { name: "Apple", type: "fruit" },
        { name: "Banana", type: "fruit" },
        { name: "Carrot", type: "vegetable" },
        { name: "Lettuce", type: "vegetable" },
        { name: "Orange", type: "fruit" },
        { name: "Tomato", type: "vegetable" },
        { name: "Broccoli", type: "vegetable" },
        { name: "Mango", type: "fruit" },
        { name: "Pear", type: "fruit" },
        { name: "Peach", type: "fruit" },
      ],
      fruitBasket: [],
      vegetableBasket: [],
      emojiMapping: {
        Apple: "🍎",
        Banana: "🍌",
        Carrot: "🥕",
        Lettuce: "🥬",
        Orange: "🍊",
        Tomato: "🍅",
        Broccoli: "🥦",
        Mango: "🥭",
        Pear: "🍐",
        Peach: "🍑",
      },
      message: "",
      draggingItem: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
      dragElement: null,
      gameCompleted: false, // New data property to track game completion
    };
  },
  methods: {
    getItemDisplay(item) {
      return this.emojiMapping[item.name] || item.name;
    },
    startDrag(event, item) {
      event.preventDefault();
      this.draggingItem = item;
      const touch = event.touches ? event.touches[0] : event;
      const elementRect = event.currentTarget.getBoundingClientRect();
      this.dragOffsetX = touch.clientX - elementRect.left;
      this.dragOffsetY = touch.clientY - elementRect.top;

      this.dragElement = event.currentTarget.cloneNode(true);
      this.dragElement.classList.add("dragging-item");
      document.body.appendChild(this.dragElement);
      this.dragElement.style.position = "absolute";
      this.dragElement.style.zIndex = 1000;
      this.moveAt(touch.pageX, touch.pageY);

      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("touchmove", this.onDrag);
      document.addEventListener("mouseup", this.endDrag);
      document.addEventListener("touchend", this.endDrag);
    },
    moveAt(pageX, pageY) {
      this.dragElement.style.left = pageX - this.dragOffsetX + "px";
      this.dragElement.style.top = pageY - this.dragOffsetY + "px";
    },
    onDrag(event) {
      event.preventDefault();
      const touch = event.touches ? event.touches[0] : event;
      this.moveAt(touch.pageX, touch.pageY);
    },
    endDrag(event) {
      event.preventDefault();
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("mouseup", this.endDrag);
      document.removeEventListener("touchend", this.endDrag);

      if (this.dragElement) {
        document.body.removeChild(this.dragElement);
        this.dragElement = null;
      }

      const touch = event.changedTouches ? event.changedTouches[0] : event;
      const dropTarget = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );

      if (dropTarget) {
        const basket = dropTarget.closest(".basket");
        if (basket) {
          const basketType = basket.getAttribute("data-basket-type");
          this.handleDrop(basketType);
        } else {
          this.message = `❌ Oops! Try dropping it into a basket`;
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      }

      this.draggingItem = null;
    },
    handleDrop(basketType) {
      const item = this.draggingItem;
      if (item.type === basketType) {
        this.message = `✅ Correct!`;
        this.playSound("correct");
        this.speakOutLoud(item.name);
        this.items = this.items.filter((i) => i.name !== item.name);
        if (basketType === "fruit") {
          this.fruitBasket.push(item);
        } else if (basketType === "vegetable") {
          this.vegetableBasket.push(item);
        }

        // Check if all items are sorted
        if (this.items.length === 0) {
          this.gameCompleted = true;
          // Wait for 4 seconds before navigating
          setTimeout(() => {
            this.$router.push('/fruits-and-veggies');
          }, 4000);
        }
      } else {
        this.message = `❌ Wrong basket!`;
        this.playSound("incorrect");
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
      setTimeout(() => {
        this.message = "";
      }, 2000);
    },
    playSound(type) {
      let url;
      if (type === "correct") {
        url =
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a";
      } else if (type === "incorrect") {
        url =
          "https://codeskulptor-demos.commondatastorage.googleapis.com/pang/arrow.mp3";
      }
      if (url) {
        const audio = new Audio(url);
        audio.play();
      }
    }
  },
};
</script>

<style scoped>
.drag-drop-game {
  text-align: center;
}

.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.item-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 20px;
}

.draggable-item {
  margin: 10px;
  padding: 15px;
  background-color: #f3e5f5;
  border-radius: 10px;
  cursor: grab;
  font-size: 3rem;
  user-select: none;
  touch-action: none;
}

.draggable-item:active {
  cursor: grabbing;
}

.baskets {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 50px;
}

.basket {
  width: 200px;
  max-width: 40%;
  min-height: 200px;
  border: 2px dashed #888;
  border-radius: 15px;
  padding: 10px;
  font-size: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.basket:hover {
  background-color: #f0f0f0;
}

.basket-items {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.basket-item {
  margin: 5px;
  font-size: 2rem;
}

.instruction {
  font-size: 2rem;
  margin-bottom: 20px;
}

.message {
  font-size: 1.5rem;
  margin-top: 20px;
}

.dragging-item {
  position: absolute;
  pointer-events: none;
  font-size: 3rem;
  background-color: #f3e5f5;
  border-radius: 10px;
  padding: 15px;
}

/* Responsive Styles */
@media screen and (max-width: 600px) {
  .baskets {
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }

  .basket {
    width: 30%;
  }

  .instruction {
    font-size: 1.5rem;
  }

  .draggable-item {
    font-size: 2.5rem;
    padding: 10px;
  }

  .basket-item {
    font-size: 1.8rem;
  }
}
</style>